import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
} from 'antd';
import {EditTwoTone, InfoCircleOutlined, PlusOutlined} from '@ant-design/icons';

import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../common/hooks';
import {
  chargerCategoryList,
  addChargerCategory,
  updateChargerCategory,
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  chargerCategoryStatusFilter,
} from './chargerCategorySlice';
import type {
  ADD_CHARGER_CATEGORY,
  UPDATE_CHARGER_CATEGORY,
  CHARGER_CATEGORY,
} from './chargerCategory.type';
import {formatDate} from '../../../../utils/formatDate';
import {
  batteryCategoryList,
  clearBatteryCategoryFilters,
} from '../../battery/batteryCategory/batteryCategorySlice';
import checkFormUpdate from '../../../../utils/checkFormUpdate';
import {debounce} from 'lodash';
import {
  debounceDelay,
  sortbyFilter,
  statusFilter,
  statusFilterOptions,
} from '../../../../common/constants';
const {Option} = Select;

const ChargerCategory = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.chargerCategory);
  const config = useAppSelector((state) => state.config.config);
  const [isChargerModalOpen, setIsChargerModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isUpdateModal, setIsUpdateModal] = useState(false);
  const [chargerCategoryForm] = Form.useForm();
  const batteryCategoriesOptions = useAppSelector((state) => state.batteryCategory);

  const [modalData, setModalData] = useState<CHARGER_CATEGORY>({} as CHARGER_CATEGORY);

  const handleUpdateChargerCategory = (values: UPDATE_CHARGER_CATEGORY) => {
    const batteryCategory_ids = values.battery_category_ids;
    delete values.battery_category_ids;

    const payload = checkFormUpdate(values, modalData) as UPDATE_CHARGER_CATEGORY;
    if (batteryCategory_ids?.length !== modalData.battery_categories.length) {
      payload.battery_category_ids = batteryCategory_ids;
    } else {
      for (const i in batteryCategory_ids) {
        if (batteryCategory_ids[i] !== modalData.battery_categories[i].id) {
          payload.battery_category_ids = batteryCategory_ids;
          break;
        }
      }
    }

    if (Object.keys(payload).length > 0) {
      dispatch(updateChargerCategory({payload: payload, category_id: modalData.id}))
        .unwrap()
        .then(() => {
          handleAddChargerCategoryModalClose();
          dispatch(
            chargerCategoryList({
              page_size: state.chargerCategories.page_size,
              page_number: state.chargerCategories.page_number,
              sort_by: state.chargerCategories.sort_by,
              ascending: state.chargerCategories.ascending,
              search: state.chargerCategories.search,
            }),
          );
        });
      //.catch((error) => {});
    } else {
      message.warning('No fields are updated');
    }
  };

  const handleAddChargerCategory = (values: ADD_CHARGER_CATEGORY) => {
    values.peak_current = Number(values.peak_current);
    values.peak_power = Number(values.peak_power);
    values.max_voltage = Number(values.max_voltage);

    dispatch(addChargerCategory(values))
      .unwrap()
      .then(() => {
        handleAddChargerCategoryModalClose();
        dispatch(
          chargerCategoryList({
            page_size: state.chargerCategories.page_size,
            page_number: state.chargerCategories.page_number,
            sort_by: state.chargerCategories.sort_by,
            ascending: state.chargerCategories.ascending,
            search: state.chargerCategories.search,
          }),
        );
      });
    // .catch((error) => {});
  };

  const handleAddChargerCategoryModalClose = () => {
    setIsChargerModalOpen(false);
    chargerCategoryForm.resetFields();
    dispatch(clearBatteryCategoryFilters());
  };

  useEffect(() => {
    //
    dispatch(
      chargerCategoryList({
        page_size: state.chargerCategories.page_size,
        page_number: state.chargerCategories.page_number,
        sort_by: state.chargerCategories.sort_by,
        ascending: state.chargerCategories.ascending,
        search: state.chargerCategories.search,
        is_active: state.chargerCategories.is_active,
      }),
    );
    // return () => {
    //   dispatch(clearBatteryCategoryFilters());
    // };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.chargerCategories.page_number,
    state.chargerCategories.page_size,
    state.chargerCategories.search,
    state.chargerCategories.is_active,
    state.chargerCategories.sort_by,
    state.chargerCategories.ascending,
  ]);

  const handleBatteryCategoryBouncing = debounce((e) => {
    dispatch(batteryCategoryList({search: e, is_active: true}));
  }, debounceDelay);

  const columns: ColumnsType<CHARGER_CATEGORY> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return (
          <>
            {(state.chargerCategories.page_number - 1) * state.chargerCategories.page_size +
              index +
              1}
          </>
        );
      },
    },
    {
      title: ' Name',
      dataIndex: 'name',
      sorter: true,
    },

    {
      title: ' Max Voltage',
      dataIndex: 'max_voltage',
    },
    {
      title: 'Port',
      dataIndex: 'conn_port',
    },

    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (_, record) => {
        return record.is_active === true ? (
          <Tag color='green'>Active</Tag>
        ) : (
          <Tag color='red'>Inactive</Tag>
        );
      },
    },

    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            <InfoCircleOutlined
              style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
              onClick={() => {
                setModalData(record);
                setIsInfoModalOpen(true);
              }}
            />
            {config.permissions.update_chargercategory && (
              <EditTwoTone
                style={{cursor: 'pointer', fontSize: '16px', color: 'blue', marginInline: '10px'}}
                onClick={() => {
                  dispatch(batteryCategoryList({is_active: true}));
                  setModalData(record);
                  setIsUpdateModal(true);
                  const ids = record.battery_categories.map((x) => x.id);
                  chargerCategoryForm.setFieldsValue({
                    name: record.name,
                    peak_current: record.peak_current,
                    peak_power: record.peak_power,
                    charger_topology: record.charger_topology,
                    conn_port: record.conn_port,
                    com_protocol: record.com_protocol,
                    thermal_points: record.thermal_points,
                    dimensions: record.dimensions,
                    varient: record.varient,
                    max_voltage: record.max_voltage,
                    battery_category_ids: ids,
                    is_active: record.is_active,
                  });
                  setIsChargerModalOpen(true);
                }}
              />
            )}
          </>
        );
      },
    },
  ];

  const handleSearch = (e: string) => {
    if (e.length > 3) {
      dispatch(searchBy(e));
    } else if (e.length === 0) {
      dispatch(searchBy(e));
    }
  };

  return (
    <div>
      <div
        style={{
          margin: '10px 0px 10px 0px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '350px  1fr 1fr',
        }}
      >
        <Input.Search
          placeholder='Search'
          defaultValue={state.chargerCategories.search}
          style={{borderRadius: '10px'}}
          allowClear
          onSearch={(e) => handleSearch(e)}
        />
        <Select
          style={{width: '100px', marginLeft: '5px'}}
          value={
            state.chargerCategories.is_active === undefined
              ? statusFilter.ALL
              : state.chargerCategories.is_active === true
              ? statusFilter.ACTIVE
              : statusFilter.INACTIVE
          }
          options={statusFilterOptions}
          onChange={(value) => {
            if (value === statusFilter.ACTIVE) {
              dispatch(chargerCategoryStatusFilter(true));
            } else if (value === statusFilter.ALL) {
              dispatch(chargerCategoryStatusFilter(undefined));
            } else {
              dispatch(chargerCategoryStatusFilter(false));
            }
          }}
          showArrow
        />
        <div style={{width: '100%', textAlign: 'end'}}>
          {config.permissions.add_chargercategory ? (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              style={{borderRadius: '5px', marginInline: '5px'}}
              onClick={() => {
                setIsUpdateModal(false);
                setIsChargerModalOpen(true);
                dispatch(batteryCategoryList({is_active: true}));
              }}
            >
              Add Category
            </Button>
          ) : null}
        </div>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        pagination={
          {
            total: state.chargerCategories.totalDataCount,
            current: state.chargerCategories.page_number,
            pageSize: state.chargerCategories.page_size,
            showSizeChanger: true,
            // onChange: (pageNumber, pageSize) => {
            //   if (pageSize !== state.chargerCategories.page_size) {
            //     dispatch(pageSizeChange({pageNumber, pageSize}));
            //   } else {
            //     dispatch(pageNumberChange(pageNumber));
            //   }
            // },
          } as TablePaginationConfig
        }
        loading={state.isLoading}
        size='middle'
        dataSource={state.chargerCategories.pageData}
        scroll={{x: undefined, y: '60vh'}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(pagination, _, {order, field}: any) => {
          if (pagination.pageSize && pagination.pageSize !== state.chargerCategories.page_size) {
            dispatch(pageSizeChange(pagination.pageSize));
          }
          if (pagination.current && pagination.current !== state.chargerCategories.page_number) {
            dispatch(pageNumberChange(pagination.current));
          }
          if (field) {
            if (order === sortbyFilter.DESCEND) {
              dispatch(sortBy({ascending: false, sortBy: field}));
            } else {
              dispatch(sortBy({ascending: true, sortBy: field}));
            }
          }
        }}
      />
      <Modal
        title={`${modalData.name}`}
        open={isInfoModalOpen}
        onCancel={() => {
          setIsInfoModalOpen(false);
        }}
        footer={null}
        width={800}
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label='Name' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.name}
          </Descriptions.Item>
          <Descriptions.Item label='Status' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.is_active ? (
              <Tag color='green'>Active</Tag>
            ) : (
              <Tag color='red'>Inactive</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label='Created By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.created_by}
          </Descriptions.Item>
          <Descriptions.Item label='Created At' labelStyle={{fontWeight: 'bolder'}}>
            {formatDate(modalData.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label='Updated By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.updated_by}
          </Descriptions.Item>
          <Descriptions.Item label='Updated At' labelStyle={{fontWeight: 'bolder'}}>
            {formatDate(modalData.updated_at)}
          </Descriptions.Item>
          <Descriptions.Item label='Peak Current (Amp.)' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.peak_current}
          </Descriptions.Item>
          <Descriptions.Item label='Peak Power (Watt)' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.peak_power}
          </Descriptions.Item>
          <Descriptions.Item label='Charger Topologyy' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.charger_topology}
          </Descriptions.Item>
          <Descriptions.Item label='Thermal point' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.thermal_points}
          </Descriptions.Item>
          <Descriptions.Item label='Dimension' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.dimensions}
          </Descriptions.Item>
          <Descriptions.Item label='Max Voltage (Volt)' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.max_voltage}
          </Descriptions.Item>
          <Descriptions.Item label='Varient' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.varient}
          </Descriptions.Item>
          <Descriptions.Item label='Conn. Port' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.conn_port}
          </Descriptions.Item>
          <Descriptions.Item label='Communication Protocols' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.com_protocol?.map((x) => {
              return <>{x.toUpperCase()}, </>;
            })}
          </Descriptions.Item>
          <Descriptions.Item label='Battery Categories' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.battery_categories?.map((x) => {
              return <>{x.name}, </>;
            })}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
      <Modal
        title={isUpdateModal ? 'Update Charger Caegory' : 'Add Charger Category'}
        open={isChargerModalOpen}
        maskClosable={false}
        onCancel={() => {
          // chargerCategoryForm.resetFields();
          setIsChargerModalOpen(false);
        }}
        okText={isUpdateModal ? 'Update Category' : 'Add Category'}
        width={800}
        confirmLoading={state.isLoading}
        onOk={() => {
          chargerCategoryForm
            .validateFields()
            .then((values) => {
              //chargerCategoryForm.resetFields();
              {
                isUpdateModal
                  ? handleUpdateChargerCategory(values)
                  : handleAddChargerCategory(values);
              }
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Row>
          <Col span={24}>
            <Form name='updateChargerCategory' form={chargerCategoryForm} layout='vertical'>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='name'
                    label='Category Name'
                    rules={[
                      {required: true, message: 'Please fill Name '},
                      {min: 4, message: 'Minimum length is 4 characters'},
                    ]}
                  >
                    <Input autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='varient'
                    label='Varient'
                    rules={[{required: true, message: 'Please fill Varient '}]}
                  >
                    <Input autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='peak_current'
                    label={
                      <>
                        Peak Current <span style={{color: '#1890FF'}}>(Amp.)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Peak Current '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='peak_power'
                    label={
                      <>
                        Peak Power <span style={{color: '#1890FF'}}>(Watt)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Varient '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='max_voltage'
                    label={
                      <>
                        Max. Voltage <span style={{color: '#1890FF'}}>(Volt)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Max. Voltaeg '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='charger_topology'
                    label='Charger Topology'
                    rules={[{required: true, message: 'Please fill Topology '}]}
                  >
                    <Input autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='dimensions'
                    label='Dimension '
                    rules={[{required: true, message: 'Please fill dimension'}]}
                  >
                    <Input autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='thermal_points'
                    label='Thermal Points'
                    rules={[{required: true, message: 'Please fill Thermal point '}]}
                  >
                    <Input autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={11}>
                  <Form.Item
                    label='Communication Protocols'
                    name='com_protocol'
                    rules={[{required: true, message: 'Select atleast 1 option'}]}
                  >
                    <Select
                      mode='multiple'
                      allowClear
                      filterOption={false}
                      placeholder='Please select'
                      listHeight={200}
                    >
                      {config.config.connection_protocol_options.map((x) => (
                        <Option key={x.value}>{x.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='conn_port'
                    label='Connection Port'
                    rules={[{required: true, message: 'Please fill Port '}]}
                  >
                    <Select placeholder='Please Select' filterOption={false}>
                      {config.config.connection_type_options?.map((x) => (
                        <Option key={x.value}>{x.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='battery_category_ids'
                    label='Battery Categories '
                    rules={[{required: true, message: 'Select atleast 1 option'}]}
                  >
                    <Select
                      mode='multiple'
                      allowClear
                      placeholder='Please select'
                      listHeight={200}
                      showSearch
                      defaultActiveFirstOption={false}
                      showArrow={false}
                      filterOption={false}
                      onSearch={(e) => {
                        if (e.length > 3) handleBatteryCategoryBouncing(e);
                      }}
                      // onChange={handleChange}
                      notFoundContent={null}
                      loading={batteryCategoriesOptions.isLoading}
                    >
                      {batteryCategoriesOptions.batteryCategories.pageData.map((x) => (
                        <Option key={x.id} value={x.id}>
                          {x.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                {isUpdateModal && (
                  <Col span={11} offset={2}>
                    <Form.Item name='is_active' label='Status' valuePropName='checked'>
                      <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default ChargerCategory;
